// components/settings/surfox-settings/sections/phone-numbers/ActiveNumbersTab.jsx
// Verbatim move of the original PhoneNumbersSection content (Phase A).
// Logic, state, handlers, and JSX are unchanged from the pre-refactor file.
// Import paths shifted by one level because this file lives in a subdirectory.

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  Plus,
  Search,
  MapPin,
  Trash2,
  AlertCircle,
  CheckCircle,
  Info,
  Loader2,
  X,
  Shield,
  MessageSquare,
  PhoneCall,
  TrendingUp,
  ExternalLink,
  Target
} from 'lucide-react';
import { useAuth } from '../../../../../context/AuthContext';
import { PERMISSIONS } from '../../../../../lib/permissions';
import supabase from '../../../../../lib/supabaseClient';
import { phoneActionErrorText } from '../../../../../lib/edgeErrorMessage';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ActiveNumbersTab({ onUpdate }) {
  const { user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transferringNumberId, setTransferringNumberId] = useState(null);
  const [showTransferConfirm, setShowTransferConfirm] = useState(null);
  const [isTrialTenant, setIsTrialTenant] = useState(false);

  // global_admin cross-tenant view: pick another tenant to see/manage THEIR numbers.
  // RLS already lets global_admin read any tenant's phone_numbers, and the
  // phone_numbers edge fn honors a body.tenant_id override for global_admin — so
  // this is a pure UI affordance that repoints the existing list + actions.
  const isGlobalAdmin = user?.role === 'global_admin';
  const [adminTenantId, setAdminTenantId] = useState('');
  const [adminTenants, setAdminTenants] = useState([]);
  const effectiveTenantId = (isGlobalAdmin && adminTenantId) ? adminTenantId : user?.tenant_id;

  // Permission checks
  const canViewPhoneNumbers = hasPermission(PERMISSIONS.VIEW_PHONE_NUMBERS);
  const canManagePhoneNumbers = hasPermission(PERMISSIONS.MANAGE_PHONE_NUMBERS);
  const canAssignPhoneNumbers = hasPermission(PERMISSIONS.ASSIGN_PHONE_NUMBERS);

  // Phone numbers state
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // A2P state
  const [a2pStatus, setA2pStatus] = useState(null);

  // Purchase modal state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedAreaCode, setSelectedAreaCode] = useState('');
  const [customAreaCode, setCustomAreaCode] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [popularAreaCodes, setPopularAreaCodes] = useState([]);

  // Stats
  const [stats, setStats] = useState({
    totalNumbers: 0,
    monthlyCost: 0
  });

  useEffect(() => {
    if (canViewPhoneNumbers) {
      loadPopularAreaCodes();
      loadA2pData();
      checkTrialStatus();
      if (isGlobalAdmin) loadAdminTenants();
    } else {
      setLoading(false);
    }
  }, [canViewPhoneNumbers]);

  // Reload the numbers list whenever the effective tenant changes (global_admin
  // switching tenants) — also covers the initial load.
  useEffect(() => {
    if (canViewPhoneNumbers && effectiveTenantId) loadPhoneNumbers();
  }, [canViewPhoneNumbers, effectiveTenantId]);

  const loadAdminTenants = async () => {
    try {
      const { data } = await supabase.from('tenants').select('id, name').order('name');
      if (Array.isArray(data)) setAdminTenants(data);
    } catch (e) {
      console.log('Could not load tenant list for admin selector');
    }
  };

  const loadA2pData = async () => {
    if (!canViewPhoneNumbers) return;

    try {
      const { data: statusData, error: statusError } = await supabase.functions.invoke('get-a2p-status');
      if (!statusError && statusData && statusData.success) {
        setA2pStatus(statusData);
      }
    } catch (a2pError) {
      console.log('A2P features not configured - skipping A2P status loading');
    }

    try {
      const { data: assignmentData, error: assignmentError } = await supabase
        .from('phone_number_campaigns')
        .select(`
          phone_number_id,
          a2p_campaign_id,
          a2p_campaigns (
            id,
            campaign_name,
            registration_status
          )
        `);

      if (!assignmentError && assignmentData && Array.isArray(assignmentData)) {
        setPhoneNumbers(prevNumbers =>
          prevNumbers.map(phone => ({
            ...phone,
            a2p_assignment: assignmentData.find(a => a.phone_number_id === phone.id)
          }))
        );
      }
    } catch (assignmentError) {
      console.log('A2P assignment tables not configured - skipping A2P assignments');
    }
  };

  const loadPhoneNumbers = async () => {
    if (!canViewPhoneNumbers) return;

    setLoading(true);
    try {
      const { data: phoneData, error: phoneError } = await supabase
        .from('phone_numbers')
        .select(`
          *,
          campaigns (
            id,
            name,
            is_active
          )
        `)
        .eq('tenant_id', effectiveTenantId)
        .order('created_at', { ascending: false });

      if (phoneError) {
        console.error('Error loading phone numbers:', phoneError);
        setError('Failed to load phone numbers: ' + phoneError.message);
        return;
      }

      const phoneNumbersData = phoneData || [];
      setPhoneNumbers(phoneNumbersData);

      const assignedToCampaigns = phoneNumbersData.filter(n => n.campaigns && n.campaigns.length > 0).length;
      setStats(prevStats => ({
        ...prevStats,
        totalNumbers: phoneNumbersData.length,
        assignedToCampaigns,
        unassignedNumbers: phoneNumbersData.length - assignedToCampaigns,
        monthlyCost: phoneNumbersData.length * 1.15
      }));

    } catch (error) {
      console.error('Error loading phone numbers:', error);
      setError('Failed to load phone numbers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadPopularAreaCodes = async () => {
    if (!canViewPhoneNumbers) return;

    try {
      const { data, error } = await supabase.functions.invoke('phone_numbers', {
        body: { action: 'popular-area-codes' }
      });

      if (!error && data && data.areaCodes && Array.isArray(data.areaCodes)) {
        setPopularAreaCodes(data.areaCodes);
      } else {
        setDefaultAreaCodes();
      }
    } catch (error) {
      setDefaultAreaCodes();
    }
  };

  const setDefaultAreaCodes = () => {
    setPopularAreaCodes([
      { code: '212', city: 'New York, NY', available: true },
      { code: '415', city: 'San Francisco, CA', available: true },
      { code: '305', city: 'Miami, FL', available: true },
      { code: '972', city: 'Dallas, TX', available: true },
      { code: '404', city: 'Atlanta, GA', available: true },
      { code: '602', city: 'Phoenix, AZ', available: true }
    ]);
  };

  const checkTrialStatus = async () => {
    if (!user?.tenant_id) return;

    const { data: tenant } = await supabase
      .from('tenants')
      .select('twilio_subaccount_sid, twilio_trial_subaccount_sid')
      .eq('id', user.tenant_id)
      .single();

    setIsTrialTenant(
      !!tenant?.twilio_trial_subaccount_sid &&
      tenant?.twilio_subaccount_sid === tenant?.twilio_trial_subaccount_sid
    );
  };

  const isTrialAccount = () => isTrialTenant;
  const hasApprovedA2P = () => true;

  const handleTransferNumber = async (phoneId) => {
    setTransferringNumberId(phoneId);

    try {
      const { data, error } = await supabase.functions.invoke('phone_numbers', {
        body: {
          action: 'transfer',
          phoneId: phoneId,
          // global_admin acting on another tenant (ignored by the fn for non-admins).
          ...(isGlobalAdmin && adminTenantId ? { tenant_id: adminTenantId } : {}),
        }
      });

      if (error) throw error;

      if (data && data.success) {
        setSuccess('Phone number transferred successfully to your dedicated account!');
        setShowTransferConfirm(null);
        loadPhoneNumbers();
        onUpdate?.();
      } else {
        throw new Error(data?.message || 'Transfer failed');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      setError(await phoneActionErrorText(error, 'Failed to transfer phone number: '));
    } finally {
      setTransferringNumberId(null);
    }
  };

  const searchPhoneNumbers = async (areaCode) => {
    if (!canManagePhoneNumbers) {
      setError("You don't have permission to purchase phone numbers.");
      return;
    }

    try {
      setSearchingNumbers(true);
      setError('');

      const { data, error } = await supabase.functions.invoke('phone_numbers', {
        body: { action: 'search', areaCode }
      });

      if (error) throw error;

      const availableNumbersData = data?.availableNumbers || [];
      if (availableNumbersData.length === 0) {
        setError(`No phone numbers available in area code ${areaCode}. Try a different area code.`);
        return;
      }

      setAvailableNumbers(availableNumbersData);
      setSelectedAreaCode(areaCode);
    } catch (error) {
      console.error('Error searching phone numbers:', error);
      setError(await phoneActionErrorText(error, 'Failed to search phone numbers: '));
    } finally {
      setSearchingNumbers(false);
    }
  };

  const purchasePhoneNumber = async (phoneNumber) => {
    if (!canManagePhoneNumbers) {
      setError("You don't have permission to purchase phone numbers.");
      return;
    }

    try {
      setPurchasing(true);
      setError('');

      const invokePromise = supabase.functions.invoke('phone_numbers', {
        body: {
          action: 'purchase',
          phoneNumber: phoneNumber.phoneNumber
        }
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT')), 45000)
      );

      const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

      if (error) throw error;

      if (data && data.success) {
        setSuccess('Phone number purchased successfully!');
        setShowPurchaseModal(false);
        setAvailableNumbers([]);
        setSelectedAreaCode('');
        loadPhoneNumbers();
        onUpdate?.();
      } else {
        throw new Error(data?.message || 'Purchase failed');
      }
    } catch (error) {
      if (error.message === 'TIMEOUT') {
        setError('Purchase is taking longer than expected. Please refresh. Your number may have already been purchased.');
        setShowPurchaseModal(false);
        setAvailableNumbers([]);
        setSelectedAreaCode('');
        loadPhoneNumbers();
      } else {
        console.error('Error purchasing phone number:', error);
        setError(await phoneActionErrorText(error, 'Failed to purchase phone number: '));
      }
    } finally {
      setPurchasing(false);
    }
  };

  const releasePhoneNumber = async (phoneId) => {
    if (!canManagePhoneNumbers) {
      setError("You don't have permission to release phone numbers.");
      return;
    }

    if (!window.confirm('Are you sure you want to release this phone number? This action cannot be undone.')) {
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('phone_numbers', {
        body: {
          action: 'release',
          phoneId,
          ...(isGlobalAdmin && adminTenantId ? { tenant_id: adminTenantId } : {}),
        }
      });

      if (error) throw error;

      if (data && data.success) {
        setSuccess('Phone number released successfully!');
        loadPhoneNumbers();
        onUpdate?.();
      } else {
        throw new Error(data?.message || 'Release failed');
      }
    } catch (error) {
      console.error('Error releasing phone number:', error);
      setError(await phoneActionErrorText(error, 'Failed to release phone number: '));
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.substring(1);
      return `(${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6)}`;
    }
    return phoneNumber;
  };

  const getAreaCode = (phoneNumber) => {
    if (!phoneNumber) return '---';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length >= 4) {
      return cleaned.startsWith('1') ? cleaned.substring(1, 4) : cleaned.substring(0, 3);
    }
    return '---';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  // Calculate stats
  useEffect(() => {
    const assignedToCampaigns = phoneNumbers.filter(n => n.campaigns && n.campaigns.length > 0).length;
    setStats({
      totalNumbers: phoneNumbers.length,
      assignedToCampaigns,
      unassignedNumbers: phoneNumbers.length - assignedToCampaigns,
      monthlyCost: phoneNumbers.length * 1.15
    });
  }, [phoneNumbers]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const filteredNumbers = phoneNumbers.filter(number => {
    if (!number) return false;
    return number.phone_number?.includes(searchTerm) || false;
  });

  // Permission check
  if (!canViewPhoneNumbers) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">Access Restricted</p>
        <p className="text-sm text-slate-400 mt-1">You don't have permission to view phone numbers.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">{success}</span>
        </div>
      )}

      {/* Permission Warning */}
      {!canManagePhoneNumbers && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-amber-700">
            You have read-only access. Admin permissions required to purchase or release numbers.
          </span>
        </div>
      )}

      {/* A2P Compliance Alert */}
      {(!a2pStatus?.brand || a2pStatus.brand.status !== 'VERIFIED') && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">A2P Compliance Required</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  SMS messaging requires A2P brand and campaign registration.
                </p>
              </div>
            </div>
            <button
              onClick={() => window.open('/settings/a2p-compliance', '_blank')}
              className="px-3 py-1.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors flex items-center"
            >
              Setup A2P
              <ExternalLink className="w-3 h-3 ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.totalNumbers}</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Assigned</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.assignedToCampaigns || 0}</p>
            </div>
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Available</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.unassignedNumbers || 0}</p>
            </div>
            <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>

      </div>

      {/* global_admin: view/manage another tenant's numbers */}
      {isGlobalAdmin && (
        <div className="flex items-center gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50">
          <Shield className="w-4 h-4 text-amber-600 flex-none" />
          <span className="text-xs font-medium text-amber-800 whitespace-nowrap">Admin — viewing tenant:</span>
          <select
            value={adminTenantId}
            onChange={(e) => setAdminTenantId(e.target.value)}
            className="flex-1 max-w-xs px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-amber-500/30"
          >
            <option value="">My own tenant</option>
            {adminTenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name || t.id}</option>
            ))}
          </select>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search phone numbers..."
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
          />
        </div>

        {canManagePhoneNumbers && (
          <button
            onClick={() => setShowPurchaseModal(true)}
            className="flex items-center space-x-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl
                       text-sm font-medium hover:bg-slate-800 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Add Number</span>
          </button>
        )}
      </div>

      {/* Phone Numbers List */}
      {filteredNumbers.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <Phone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">
            {phoneNumbers.length === 0 ? 'No phone numbers yet' : 'No matching numbers'}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            {phoneNumbers.length === 0 && canManagePhoneNumbers
              ? 'Purchase your first number to start sending messages'
              : 'Try adjusting your search'
            }
          </p>
          {phoneNumbers.length === 0 && canManagePhoneNumbers && (
            <button
              onClick={() => setShowPurchaseModal(true)}
              className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800"
            >
              Purchase First Number
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredNumbers.map((number, index) => (
            <motion.div
              key={number.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="group flex items-center justify-between p-4 bg-white hover:bg-slate-50
                         rounded-xl border border-slate-200 hover:border-slate-300 transition-all"
            >
              <div className="flex items-center space-x-4">
                {/* Area Code Badge */}
                <div className="w-12 h-12 bg-slate-100 rounded-xl border border-slate-200
                                flex items-center justify-center">
                  <span className="text-sm font-bold text-slate-700">
                    {getAreaCode(number.phone_number)}
                  </span>
                </div>

                {/* Number Info */}
                <div>
                  <div className="font-semibold text-slate-900">
                    {formatPhoneNumber(number.phone_number)}
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-slate-500 mt-1">
                    {number.messaging_service_sid ? (
                      <span className="flex items-center text-emerald-600">
                        <Shield className="w-3 h-3 mr-1" />
                        A2P Compliant
                      </span>
                    ) : (
                      <span>ID: {number.twilio_sid?.slice(-6) || 'N/A'}</span>
                    )}
                    <span className="text-slate-300">·</span>
                    <span>Added {formatDate(number.purchased_at)}</span>
                  </div>
                </div>
              </div>

              {/* Capabilities & Actions */}
              <div className="flex items-center space-x-3">
                {/* Capabilities */}
                <div className="flex items-center space-x-2 mr-2">
                  {number.capabilities?.sms && (
                    <span className="px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg flex items-center">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      SMS
                    </span>
                  )}
                  {number.capabilities?.voice && (
                    <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg flex items-center">
                      <PhoneCall className="w-3 h-3 mr-1" />
                      Voice
                    </span>
                  )}
                </div>

                {/* Status */}
                <span className={`px-2.5 py-1 text-xs font-medium rounded-lg ${
                  number.status === 'active'
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {number.status}
                </span>

                {/* Actions */}
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canManagePhoneNumbers && ((isGlobalAdmin && adminTenantId) || (isTrialAccount() && hasApprovedA2P())) && (
                    <button
                      onClick={() => setShowTransferConfirm(number.id)}
                      disabled={transferringNumberId === number.id}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Transfer to dedicated account"
                    >
                      {transferringNumberId === number.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      )}
                    </button>
                  )}

                  {canManagePhoneNumbers && (
                    <button
                      onClick={() => releasePhoneNumber(number.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Release number"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          Numbers can be assigned to campaigns for SMS messaging.
        </p>
      </div>

      {/* Purchase Modal */}
      <AnimatePresence>
        {showPurchaseModal && canManagePhoneNumbers && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowPurchaseModal(false);
              setAvailableNumbers([]);
              setSelectedAreaCode('');
              setCustomAreaCode('');
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Add Phone Number</h3>
                <button
                  onClick={() => {
                    setShowPurchaseModal(false);
                    setAvailableNumbers([]);
                    setSelectedAreaCode('');
                    setCustomAreaCode('');
                  }}
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                {/* 2026-09-17: errors (e.g. "no numbers in this area code") were
                    only ever rendered in the OUTER page body (line ~461), which
                    sits behind this modal's overlay while it's open — visually
                    invisible even though the state was set correctly. Mirrored
                    here so a search/purchase failure is actually readable at
                    the moment it happens, without touching the outer copy
                    (still needed for errors that occur with the modal closed,
                    e.g. load/transfer/release failures). */}
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}
                {availableNumbers.length === 0 ? (
                  <div className="space-y-6">
                    {/* Popular Area Codes */}
                    <div>
                      <h4 className="font-medium text-slate-900 mb-3">Select Area Code</h4>
                      {searchingNumbers ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {popularAreaCodes.map((areaCode) => (
                            <button
                              key={areaCode.code}
                              onClick={() => searchPhoneNumbers(areaCode.code)}
                              disabled={!areaCode.available}
                              className={`p-4 border rounded-xl text-left transition-all ${
                                areaCode.available
                                  ? 'border-slate-200 hover:border-orange-300 hover:bg-orange-50'
                                  : 'border-slate-100 bg-slate-50 cursor-not-allowed opacity-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-bold text-slate-900">({areaCode.code})</span>
                                  <div className="flex items-center text-xs text-slate-500 mt-1">
                                    <MapPin className="w-3 h-3 mr-1" />
                                    {areaCode.city}
                                  </div>
                                </div>
                                {areaCode.available && (
                                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Custom Area Code */}
                    <div className="border-t border-slate-200 pt-6">
                      <h4 className="font-medium text-slate-900 mb-3">Or Enter Custom Area Code</h4>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={customAreaCode}
                          onChange={(e) => setCustomAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                          placeholder="e.g., 555"
                          className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-slate-900
                                     focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                          maxLength="3"
                        />
                        <button
                          onClick={() => searchPhoneNumbers(customAreaCode)}
                          disabled={customAreaCode.length !== 3 || searchingNumbers}
                          className="px-6 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800
                                     disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium"
                        >
                          <Search className="w-4 h-4 mr-2" />
                          Search
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-slate-900">
                        Available in ({selectedAreaCode})
                      </h4>
                      <button
                        onClick={() => {
                          setAvailableNumbers([]);
                          setSelectedAreaCode('');
                        }}
                        className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                      >
                        Change Area Code
                      </button>
                    </div>

                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {availableNumbers.map((number) => (
                        <div
                          key={number.phoneNumber}
                          className="flex items-center justify-between p-4 border border-slate-200 rounded-xl
                                     hover:border-orange-300 hover:bg-orange-50 transition-all"
                        >
                          <div>
                            <div className="font-semibold text-slate-900">
                              {formatPhoneNumber(number.phoneNumber)}
                            </div>
                            {number.locality && (
                              <div className="flex items-center text-sm text-slate-500 mt-1">
                                <MapPin className="w-3 h-3 mr-1" />
                                {number.locality}, {number.region}
                              </div>
                            )}
                            <div className="flex items-center space-x-2 mt-2">
                              {number.capabilities?.sms && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg">SMS</span>
                              )}
                              {number.capabilities?.voice && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-lg">Voice</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <button
                              onClick={() => purchasePhoneNumber(number)}
                              disabled={purchasing}
                              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800
                                         disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              {purchasing ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Purchase'
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Transfer Confirmation Modal */}
      <AnimatePresence>
        {showTransferConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowTransferConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full"
            >
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Transfer Phone Number</h3>
              </div>

              <div className="px-6 py-6">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mx-auto mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <p className="text-slate-700 text-center mb-3">
                  This will transfer the phone number from your trial account to your dedicated account.
                </p>
                <p className="text-sm text-slate-500 text-center mb-4">
                  The number will be linked to your verified A2P campaign and ready for production use.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="flex items-start">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                      <strong>Important:</strong> This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-slate-50 rounded-b-2xl flex gap-3 justify-end">
                <button
                  onClick={() => setShowTransferConfirm(null)}
                  disabled={transferringNumberId}
                  className="px-4 py-2 text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50
                             transition-colors disabled:opacity-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleTransferNumber(showTransferConfirm)}
                  disabled={transferringNumberId}
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800
                             transition-colors disabled:opacity-50 flex items-center font-medium"
                >
                  {transferringNumberId ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Transferring...
                    </>
                  ) : (
                    'Transfer Number'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
